import { useEffect, useState } from 'react';
import { CheckCircle2, RefreshCw } from 'lucide-react';
import { Button, Card } from '@pixlide/ui';
import type { PairingCode } from '@/api/queries/screens';

interface PairingPanelProps {
  readonly screenName: string;
  readonly code: PairingCode;

  /** Vrai dès que l'appareil a saisi le code (détecté par le rafraîchissement du parc). */
  readonly isPaired: boolean;
  readonly isRegenerating: boolean;
  readonly onRegenerate: () => void;
  readonly onClose: () => void;
}

/** Durée restante au format « 14:32 ». */
function formatRemaining(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Code d'appairage à recopier sur l'écran.
 *
 * Le code est affiché dans sa forme lisible (`ACDE-FGHJ`) : le player accepte tiret et
 * minuscules, et l'alphabet du code exclut les caractères ambigus.
 */
export function PairingPanel({ screenName, code, isPaired, isRegenerating, onRegenerate, onClose }: PairingPanelProps) {
  const expiresAt = Date.parse(code.expiresAt);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (isPaired) {
      return;
    }

    const interval = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(interval);
  }, [isPaired]);

  const remaining = expiresAt - now;
  const isExpired = remaining <= 0;
  const playerUrl = import.meta.env.VITE_PLAYER_URL;

  if (isPaired) {
    return (
      <Card className="flex flex-col items-center gap-3 p-8 text-center" role="status">
        <CheckCircle2 aria-hidden className="size-10 text-success-500" />
        <h3 className="text-lg font-semibold text-ink-900">« {screenName} » est appairé</h3>
        <p className="max-w-md text-sm text-ink-500">
          L'écran est prêt. Programmez une diffusion depuis « Diffusion » : elle apparaîtra à l'écran en quelques
          secondes.
        </p>
        <Button onClick={onClose}>Terminer</Button>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col gap-6 p-6 sm:flex-row sm:items-center">
      <div className="flex flex-col items-center gap-2 rounded-card bg-ink-50 px-8 py-6 sm:min-w-72">
        <p className="text-xs font-medium tracking-wide text-ink-500 uppercase">Code d'appairage</p>
        <p
          className={`font-mono text-4xl font-semibold tracking-[0.15em] ${isExpired ? 'text-ink-300 line-through' : 'text-ink-900'}`}
          aria-label={`Code d'appairage ${code.displayCode.split('').join(' ')}`}
        >
          {code.displayCode}
        </p>
        {/* aria-live « off » : un décompte annoncé chaque seconde rendrait la page
            inutilisable au lecteur d'écran. Seule l'expiration est annoncée, ci-dessous. */}
        <p className="text-sm text-ink-500 tabular-nums" aria-live="off">
          {isExpired ? 'Code expiré' : `Valable encore ${formatRemaining(remaining)}`}
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-3">
        <h3 className="text-lg font-semibold text-ink-900">Appairer « {screenName} »</h3>

        <ol className="list-decimal space-y-1 pl-5 text-sm text-ink-700">
          <li>
            Sur la TV ou le boîtier, ouvrez{' '}
            {playerUrl ? (
              <span className="font-mono font-medium break-all text-ink-900">{playerUrl}</span>
            ) : (
              "l'application Pixlide Player"
            )}
            .
          </li>
          <li>Saisissez le code affiché ici, puis validez.</li>
          <li>Cette page se met à jour toute seule dès que l'écran est appairé.</li>
        </ol>

        {isExpired ? (
          <p role="alert" className="text-sm font-medium text-danger-500">
            Ce code a expiré. Générez-en un nouveau pour continuer.
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button variant={isExpired ? 'primary' : 'secondary'} size="sm" isLoading={isRegenerating} onClick={onRegenerate}>
            <RefreshCw aria-hidden className="size-4" />
            Nouveau code
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    </Card>
  );
}
